import {
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsProjectsEnabled,
} from '@/features/flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { formatWithMomentToken } from '@/utils/dates';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { endOfISOWeek, isValid, parseISO } from 'date-fns';

const ELIGIBLE_TIERS = [SubscriptionTier.PRO, SubscriptionTier.PRO_PLUS];
export const ADVANCED_EVIDENCE_IN_SESSION_QUOTA = 10;

export type EvidenceCreditsRemainingVariant = 'critical' | 'warning' | 'healthy';

/** Maps remaining plan credits to a progress indicator color variant. */
export function getEvidenceCreditsRemainingVariant(
  remaining: number
): EvidenceCreditsRemainingVariant {
  if (remaining === 0) return 'critical';
  if (remaining < 5) return 'warning';
  return 'healthy';
}

const EVIDENCE_CREDITS_PROGRESS_INDICATOR_CLASS: Record<
  EvidenceCreditsRemainingVariant,
  string
> = {
  critical: '[&>div]:bg-validation-destructive',
  warning: '[&>div]:bg-validation-warning',
  healthy: '[&>div]:bg-validation-success',
};

export function getEvidenceCreditsProgressIndicatorClass(remaining: number): string {
  return EVIDENCE_CREDITS_PROGRESS_INDICATOR_CLASS[
    getEvidenceCreditsRemainingVariant(remaining)
  ];
}

/**
 * Whether the "{count} left" Pro quota badge should render for the current user.
 */
export function useEvidenceInSessionLimits() {
  const isEvidenceQueryLimitsForLegacyTiersEnabled =
    useIsEvidenceQueryLimitsForLegacyTiersEnabled();
  const isHeidiIIEnabled = useIsProjectsEnabled();
  const { isInSessionEvidenceShown, isSessionlessEvidenceShown } = useEvidenceAccess();
  const hasAnyEvidenceAccess = isInSessionEvidenceShown || isSessionlessEvidenceShown;
  const { data: userData } = useQueryUser((user) => {
    return {
      advancedEvidenceInSession: user?.evidence_usage?.in_session,
      session_settings: user?.session_settings,
    };
  });
  const { data: newUserTier } = useQueryUserBillingInfo((user) => user?.new_product_tier);

  const advancedUsed = userData?.advancedEvidenceInSession?.advanced_used ?? 0;
  const advancedRemaining = Math.max(
    0,
    ADVANCED_EVIDENCE_IN_SESSION_QUOTA - advancedUsed
  );

  const currentPeriodEnd = userData?.advancedEvidenceInSession?.current_period_end;
  // Fallback uses Sunday end-of-week (endOfISOWeek) to match the BE's
  // documented anchor for Evidence quota reset (see
  // evidence-quota-toast-dev-section.tsx). A locale-defaulted endOfWeek
  // would land on Saturday in `en` — a 1-day drift from the BE anchor.
  // `isValid` guard absorbs malformed `current_period_end` payloads
  // before they reach `format`, which throws on Invalid Date.
  // Format follows Figma (10767:56356) — short `D MMM` ("23 May"), no year.
  // The reset always lands within the next week, so the year is redundant and
  // omitting it keeps the 289px popover from wrapping the date row.
  const parsedPeriodEnd = currentPeriodEnd ? parseISO(currentPeriodEnd) : null;
  const advancedResetDate =
    parsedPeriodEnd && isValid(parsedPeriodEnd)
      ? formatWithMomentToken(parsedPeriodEnd, 'D MMM')
      : formatWithMomentToken(endOfISOWeek(new Date()), 'D MMM');

  const isEligibleTier =
    isEvidenceQueryLimitsForLegacyTiersEnabled &&
    typeof advancedRemaining === 'number' &&
    !!newUserTier &&
    ELIGIBLE_TIERS.includes(newUserTier);

  const isEligibleForAdvancedLimit = isEligibleTier && isInSessionEvidenceShown;

  const isEligibleForAdvancedLimitSessionless = isEligibleTier && hasAnyEvidenceAccess;

  // Heidi II accounts are billed through the agent credit allowance, so the
  // Evidence "{count} left" badge would misstate what a send costs them.
  const showsQuotaBadge = isEligibleForAdvancedLimit && !isHeidiIIEnabled;
  const showsQuotaBadgeSessionless =
    isEligibleForAdvancedLimitSessionless && !isHeidiIIEnabled;

  return {
    isEligibleForAdvancedLimit,
    isEligibleForAdvancedLimitSessionless,
    showsQuotaBadge,
    showsQuotaBadgeSessionless,
    advancedRemaining,
    advancedUsed,
    advancedResetDate,
  };
}
